// Shared contract for the slide-3 strategy layout. One shape, four
// consumers: wizard step 3, the internal detail page, the public lead view,
// and the proposal PDF. Two normalizers build it — from live wizard state
// (strategyFromWizard) and from a persisted proposal (strategyFromProposal).

import {
    ProposalDetail,
    ProposalObjectiveDetail,
    ProposalPricingPreviewResponse,
    ProposalTier,
    SelectedChannel,
    TIER_ORDER,
    WizardContentType,
    WizardObjective,
} from "../types";

export interface StrategyUnit {
    contentTypeId: number;
    name: string | null;
    displayName: string;
    quantity: number;
    rank: number;
    reason?: string | null;
}

export interface StrategyChannel {
    id: number;
    name: string;
    quantity: number;
}

export interface StrategyPricing {
    total: number | string | null;
    projectCount: number;
    savingsPct: number;
}

export interface StrategyData {
    objectiveName: string | null;
    objectiveSlug: string | null;
    secondaryObjectiveName: string | null;
    secondaryObjectiveSlug: string | null;
    categoryName: string | null;
    tier: ProposalTier;
    channels: StrategyChannel[];
    units: StrategyUnit[];
    // Extra units the NEXT tier up would add — rendered greyed as
    // "OPTIONAL — included in {tier}" upsell rows.
    nextTierUnits: StrategyUnit[];
    nextTierLabel: string | null;
    pricing: StrategyPricing | null;
    // What the analysis behind this strategy found (PDP, CV or AI Presence).
    description?: string | null;
}

const QTY_FIELD: Record<ProposalTier, keyof WizardContentType> = {
    good: "qty_good",
    better: "qty_better",
    best: "qty_best",
};
// Matrix 3: good/better/best packages use the secondary's good/good/better
// overlay rows.
const OVERLAY_FIELD: Record<ProposalTier, keyof WizardContentType> = {
    good: "overlay_qty_good",
    better: "overlay_qty_good",
    best: "overlay_qty_better",
};

type ObjectiveLike = Pick<WizardObjective, "content_types"> | ProposalObjectiveDetail;

// Client-side mirror of the backend's contentTypeUnitsForTier: primary
// counts at the tier, secondary overlay maxed in per content type,
// secondary-only rows appended after the primary's mix.
export function computeUnits(
    objective: ObjectiveLike | null | undefined,
    secondary: ObjectiveLike | null | undefined,
    tier: ProposalTier,
): StrategyUnit[] {
    if (!objective) return [];
    const qtyField = QTY_FIELD[tier];
    const overlayField = OVERLAY_FIELD[tier];

    const byId = new Map<number, StrategyUnit & { sortKey: number }>();
    (objective.content_types || []).forEach((ct) => {
        byId.set(ct.id, {
            contentTypeId: ct.id,
            name: ct.name,
            displayName: ct.display_name,
            quantity: Number(ct[qtyField]) || 0,
            rank: ct.rank,
            reason: ct.reason,
            sortKey: ct.rank,
        });
    });
    (((secondary && secondary.content_types) || []) as WizardContentType[]).forEach((ct) => {
        const overlayQty = Number(ct[overlayField]) || 0;
        const existing = byId.get(ct.id);
        if (existing) {
            existing.quantity = Math.max(existing.quantity, overlayQty);
        } else if (overlayQty > 0) {
            byId.set(ct.id, {
                contentTypeId: ct.id,
                name: ct.name,
                displayName: ct.display_name,
                quantity: overlayQty,
                rank: ct.rank,
                reason: ct.reason,
                sortKey: 100 + ct.rank,
            });
        }
    });
    return [...byId.values()]
        .filter((u) => u.quantity > 0)
        .sort((a, b) => a.sortKey - b.sortKey)
        .map((u) => ({
            contentTypeId: u.contentTypeId,
            name: u.name,
            displayName: u.displayName,
            quantity: u.quantity,
            rank: u.rank,
            reason: u.reason,
        }));
}

export const unitCount = (units: StrategyUnit[]): number =>
    units.reduce((sum, u) => sum + u.quantity, 0);

const TIER_LABELS: Record<ProposalTier, string> = {
    good: "Good",
    better: "Better",
    best: "Best",
};

// The delta the next tier up adds: net-new content types at full quantity,
// plus quantity bumps on types already included.
function nextTierDelta(
    objective: ObjectiveLike | null | undefined,
    secondary: ObjectiveLike | null | undefined,
    tier: ProposalTier,
): { units: StrategyUnit[]; label: string | null } {
    const idx = TIER_ORDER.indexOf(tier);
    const next = TIER_ORDER[idx + 1];
    if (!next || !objective) return { units: [], label: null };
    const current = new Map(
        computeUnits(objective, secondary, tier).map((u) => [u.contentTypeId, u]),
    );
    const units: StrategyUnit[] = [];
    computeUnits(objective, secondary, next).forEach((u) => {
        const existing = current.get(u.contentTypeId);
        const delta = u.quantity - (existing ? existing.quantity : 0);
        if (delta > 0) units.push({ ...u, quantity: delta });
    });
    return { units, label: TIER_LABELS[next] };
}

export function collapseChannels(selected: SelectedChannel[]): StrategyChannel[] {
    const byId = new Map<number, StrategyChannel>();
    selected.forEach((ch) => {
        const entry = byId.get(ch.id) || { id: ch.id, name: ch.name, quantity: 0 };
        entry.quantity += 1;
        byId.set(ch.id, entry);
    });
    return [...byId.values()];
}

export function strategyFromWizard(args: {
    objective: WizardObjective | undefined;
    secondaryObjective: WizardObjective | undefined;
    categoryName: string | null;
    selectedChannels: SelectedChannel[];
    tier: ProposalTier;
    preview: ProposalPricingPreviewResponse | null;
    description?: string | null;
}): StrategyData {
    const { objective, secondaryObjective, categoryName, selectedChannels, tier, preview, description } = args;
    const units = computeUnits(objective, secondaryObjective, tier);
    const delta = nextTierDelta(objective, secondaryObjective, tier);
    const estimate = preview ? preview.tiers[tier] : undefined;
    return {
        objectiveName: objective ? objective.name : null,
        objectiveSlug: objective ? objective.slug : null,
        secondaryObjectiveName: secondaryObjective ? secondaryObjective.name : null,
        secondaryObjectiveSlug: secondaryObjective ? secondaryObjective.slug : null,
        categoryName,
        tier,
        channels: collapseChannels(selectedChannels),
        units,
        nextTierUnits: delta.units,
        nextTierLabel: delta.label,
        pricing: estimate
            ? {
                total: estimate.total,
                projectCount: estimate.projectCount,
                savingsPct: estimate.savingsPct,
            }
            : null,
        description: description || null,
    };
}

export function strategyFromProposal(detail: ProposalDetail): StrategyData {
    const tier: ProposalTier = detail.tier || "better";
    const objective = detail.objective;
    const secondary = detail.secondary_objective;

    // Persisted units are the source of truth (they're what was priced);
    // enrich with rank/reason from the objective's content-type config.
    const metaById = new Map<number, WizardContentType>();
    (((objective && objective.content_types) || []) as WizardContentType[]).forEach((ct) => {
        metaById.set(ct.id, ct);
    });
    (((secondary && secondary.content_types) || []) as WizardContentType[]).forEach((ct) => {
        if (!metaById.has(ct.id)) metaById.set(ct.id, ct);
    });
    const units: StrategyUnit[] = detail.content_type_units.map((u, i) => {
        const meta = metaById.get(u.content_type_id);
        return {
            contentTypeId: u.content_type_id,
            name: u.name,
            displayName: u.display_name || u.name || "Expert video",
            quantity: u.quantity,
            rank: meta ? meta.rank : i + 1,
            reason: meta ? meta.reason : null,
        };
    });
    units.sort((a, b) => a.rank - b.rank);

    const delta = detail.tier
        ? nextTierDelta(objective, secondary, tier)
        : { units: [], label: null };

    return {
        objectiveName: objective ? objective.name : null,
        objectiveSlug: objective ? objective.slug : null,
        secondaryObjectiveName: secondary ? secondary.name : null,
        secondaryObjectiveSlug: secondary ? secondary.slug : null,
        categoryName: detail.product_category ? detail.product_category.name : null,
        tier,
        channels: detail.channels,
        units,
        nextTierUnits: delta.units,
        nextTierLabel: delta.label,
        pricing: {
            total: detail.pricing.total,
            projectCount: detail.projects.length,
            savingsPct: detail.pricing.savings_pct,
        },
        description: detail.description || null,
    };
}
